/**
 * Celebration Store - Manages streak milestone celebrations
 * Tracks when to show celebrations and which milestones have been acknowledged
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type CelebrationType = 'streak' | 'target_complete';

export interface CelebrationData {
  type: CelebrationType;
  value: number;
  message: string;
  subMessage?: string;
}

interface CelebrationState {
  activeCelebration: CelebrationData | null;
  acknowledgedMilestones: number[]; // Streak milestones already celebrated
  lastCelebratedDate: string | null;
}

interface CelebrationActions {
  showCelebration: (celebration: CelebrationData) => void;
  dismissCelebration: () => void;
  checkStreakMilestone: (streak: number) => CelebrationData | null;
  resetDailyMilestones: () => void;
}

type CelebrationStore = CelebrationState & CelebrationActions;

// Streak milestones to celebrate
const STREAK_MILESTONES = [5, 10, 20, 30, 50, 75, 100];

const getStreakMessage = (streak: number): { message: string; subMessage: string } => {
  if (streak >= 100) {
    return {
      message: 'LEGENDARY!',
      subMessage: `${streak} day streak - You're unstoppable!`,
    };
  }
  if (streak >= 75) {
    return {
      message: 'PHENOMENAL!',
      subMessage: `${streak} day streak - Elite performer!`,
    };
  }
  if (streak >= 50) {
    return {
      message: 'INCREDIBLE!',
      subMessage: `${streak} day streak - True dedication!`,
    };
  }
  if (streak >= 30) {
    return {
      message: 'OUTSTANDING!',
      subMessage: `${streak} days strong - A whole month!`,
    };
  }
  if (streak >= 20) {
    return {
      message: 'IMPRESSIVE!',
      subMessage: `${streak} day streak - Keep crushing it!`,
    };
  }
  if (streak >= 10) {
    return {
      message: 'WELL DONE!',
      subMessage: `${streak} days in a row - Double digits!`,
    };
  }
  if (streak >= 5) {
    return {
      message: 'NICE STREAK!',
      subMessage: `${streak} days - Building momentum!`,
    };
  }
  return {
    message: 'KEEP GOING!',
    subMessage: `${streak} day streak`,
  };
};

const getTodayDate = (): string => {
  return new Date().toISOString().split('T')[0] ?? '';
};

export const useCelebrationStore = create<CelebrationStore>()(
  persist(
    (set, get) => ({
      // State
      activeCelebration: null,
      acknowledgedMilestones: [],
      lastCelebratedDate: null,

      // Actions
      showCelebration: (celebration) => set({ activeCelebration: celebration }),

      dismissCelebration: () => set({ activeCelebration: null }),

      checkStreakMilestone: (streak) => {
        const { lastCelebratedDate } = get();
        const today = getTodayDate();

        // Reset milestones if it's a new day
        if (lastCelebratedDate !== today) {
          set({ acknowledgedMilestones: [], lastCelebratedDate: today });
        }

        // Check if this streak hits a milestone we haven't celebrated today
        const milestone = STREAK_MILESTONES.find(
          (m) => streak >= m && !get().acknowledgedMilestones.includes(m)
        );

        if (milestone) {
          const { message, subMessage } = getStreakMessage(streak);

          // Mark this milestone as acknowledged
          set((state) => ({
            acknowledgedMilestones: [...state.acknowledgedMilestones, milestone],
          }));

          return {
            type: 'streak',
            value: streak,
            message,
            subMessage,
          };
        }

        return null;
      },

      resetDailyMilestones: () =>
        set({
          acknowledgedMilestones: [],
          lastCelebratedDate: getTodayDate(),
        }),
    }),
    {
      name: 'pulse-celebrations',
      partialize: (state) => ({
        acknowledgedMilestones: state.acknowledgedMilestones,
        lastCelebratedDate: state.lastCelebratedDate,
      }),
    }
  )
);
